// Deterministic MIR core — the shared analyzer (pure, runs in Node and the browser).
// MIDI notes → inferred key + the set of chord atoms used. This is the calibration-
// proof half of the tagging pipeline (the GPU judge adds the fuzzy curriculum tags on
// top); it also powers the coverage tool and bring-your-own-song placement. Chord
// recognition is approximate (pitch-class template matching per bar) — fine for
// binning a piece onto the lattice.

import { keyFromNotes } from "../render/staff-layout";
import type { NoteEvent } from "../types";
import { chordAtomId } from "./chord-atoms";
import type { ChordQuality } from "./note-math";

const TEMPLATES: Record<ChordQuality, number[]> = {
  maj: [0, 4, 7],
  min: [0, 3, 7],
  dom7: [0, 4, 7, 10],
};

const pc = (midi: number): number => ((Math.round(midi) % 12) + 12) % 12;

/**
 * The best-fitting chord atom for a set of sounding pitches (pitch classes only —
 * octave/inversion agnostic), or `null` when no full triad is present. Requires the
 * root·third·fifth to all sound, then rewards template coverage and penalizes
 * out-of-chord notes, so dom7 wins over its major triad when the seventh is there.
 */
export function bestChordAtom(pitches: readonly number[]): string | null {
  const set = new Set(pitches.map(pc));
  let best: string | null = null;
  let bestScore = Number.NEGATIVE_INFINITY;
  for (let root = 0; root < 12; root++) {
    for (const q of ["maj", "min", "dom7"] as ChordQuality[]) {
      const tmpl = TEMPLATES[q].map((o) => (root + o) % 12);
      const triad = tmpl.slice(0, 3);
      if (!triad.every((t) => set.has(t))) continue; // need the full triad
      const present = tmpl.filter((t) => set.has(t)).length;
      const extra = [...set].filter((p) => !tmpl.includes(p)).length;
      const score = present * 2 - extra; // coverage rewarded, outliers penalized
      if (score > bestScore) {
        bestScore = score;
        best = chordAtomId(root, q);
      }
    }
  }
  return best;
}

/** Pitches whose onset falls within `[lo, hi)`. */
function pitchesInBar(notes: readonly NoteEvent[], lo: number, hi: number): number[] {
  return notes.filter((nt) => nt.onset >= lo && nt.onset < hi).map((nt) => nt.midi);
}

interface MidiAnalysis {
  /** Key as a line-of-fifths position (+sharps / −flats), from `keyFromNotes`. */
  keyFifths: number;
  /** Chord atom ids used, deduped, in order of first appearance. */
  chords: string[];
}

/** Per-bar best chord atom in order, bars with no full triad dropped — the chord *sequence*
 *  (with repeats), for progression detection (`analyzeMidi.chords` dedupes and loses order). */
export function barChordAtoms(notes: readonly NoteEvent[], bars: readonly number[]): string[] {
  const out: string[] = [];
  for (let i = 0; i + 1 < bars.length; i++) {
    const atom = bestChordAtom(pitchesInBar(notes, bars[i] ?? 0, bars[i + 1] ?? 0));
    if (atom) out.push(atom);
  }
  return out;
}

/** Per-bar best chord atom aligned to bar index — `null` where a bar has no full
 *  triad. Length = bars.length − 1. The dense counterpart to `barChordAtoms` (which
 *  drops empties); needed to place a lead-sheet symbol over its own measure. */
export function barChordAtomsDense(
  notes: readonly NoteEvent[],
  bars: readonly number[],
): (string | null)[] {
  const out: (string | null)[] = [];
  for (let i = 0; i + 1 < bars.length; i++) {
    out.push(bestChordAtom(pitchesInBar(notes, bars[i] ?? 0, bars[i + 1] ?? 0)));
  }
  return out;
}

/** Analyze parsed notes (+ barline times) into key + chord-atom vocabulary. */
export function analyzeMidi(notes: readonly NoteEvent[], bars: readonly number[]): MidiAnalysis {
  const chords: string[] = [];
  const seen = new Set<string>();
  for (let i = 0; i + 1 < bars.length; i++) {
    const atom = bestChordAtom(pitchesInBar(notes, bars[i] ?? 0, bars[i + 1] ?? 0));
    if (atom && !seen.has(atom)) {
      seen.add(atom);
      chords.push(atom);
    }
  }
  return { keyFifths: keyFromNotes(notes).fifths, chords };
}
